import { useEffect, useState, type FormEvent, type InputHTMLAttributes } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { ApiError } from '../api/client';
import { AuthLayout } from '../components/AuthLayout';
import { Ikon } from '../components/icons';
import { Alert, Field } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { provelengde } from '../lib/plans';
import { SIDETITLER, useSidehode } from '../lib/sidehode';
import { meld } from '../lib/trakt';

export function RegisterPage() {
  const { register, status, weddings } = useAuth();
  const navigate = useNavigate();
  useSidehode(SIDETITLER.registrer, '/registrer');
  // Ett signal per visning. Se `lib/trakt.ts` – ingenting lagres i nettleseren.
  useEffect(() => {
    meld('registrering_apnet');
  }, []);

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    acceptTerms: false,
    marketingConsent: false,
  });
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  // Bekreftelsen sendes ikke til serveren – den finnes bare for å fange en
  // skrivefeil før kontoen opprettes med et passord ingen vet hva er.
  const [confirmPassword, setConfirmPassword] = useState('');
  // Feilen vises først når man har forlatt feltet (eller prøvd å sende inn).
  // Å få «ikke like» i ansiktet etter første tegn er bare masete.
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to={weddings.length > 0 ? '/app' : '/kom-i-gang'} replace />;
  }

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const passwordsDiffer = confirmPassword !== form.password;
  const confirmError =
    confirmTouched && passwordsDiffer ? 'Passordene er ikke like.' : undefined;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (passwordsDiffer) {
      setConfirmTouched(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await register(form);
      navigate('/kom-i-gang');
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, { code: 'unknown', message: 'Klarte ikke å opprette konto.' }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Opprett konto"
      subtitle={`Start planleggingen gratis, og prøv alle verktøyene i Plus i ${provelengde()}. Du trenger ikke betalingskort.`}
    >
      <form onSubmit={onSubmit} noValidate>
        {error && !error.fields?.length ? <Alert>{error.message}</Alert> : null}

        <Field label="Navnet ditt" error={error?.fieldError('name')}>
          <input
            className="input"
            autoComplete="name"
            required
            value={form.name}
            onChange={(event) => update('name', event.target.value)}
          />
        </Field>

        <Field label="E-post" error={error?.fieldError('email')}>
          <input
            className="input"
            type="email"
            autoComplete="email"
            required
            value={form.email}
            onChange={(event) => update('email', event.target.value)}
          />
        </Field>

        <Field
          label="Passord"
          hint="Minst 10 tegn"
          error={error?.fieldError('password')}
        >
          <PasswordInput
            visible={showPassword}
            onToggle={() => setShowPassword((current) => !current)}
            autoComplete="new-password"
            required
            minLength={10}
            value={form.password}
            onChange={(event) => update('password', event.target.value)}
          />
        </Field>

        <Field label="Bekreft passord" error={confirmError}>
          <PasswordInput
            visible={showConfirmPassword}
            onToggle={() => setShowConfirmPassword((current) => !current)}
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            onBlur={() => setConfirmTouched(true)}
            aria-invalid={confirmError ? true : undefined}
          />
        </Field>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.acceptTerms}
            onChange={(event) => update('acceptTerms', event.target.checked)}
          />
          <span>
            Jeg godtar{' '}
            <Link to="/vilkaar" target="_blank">
              bruksvilkårene
            </Link>{' '}
            og har lest{' '}
            <Link to="/personvern" target="_blank">
              personvernerklæringen
            </Link>
            .
          </span>
        </label>
        {error?.fieldError('acceptTerms') ? (
          <p className="field-error" style={{ marginTop: '-0.4rem' }}>
            {error.fieldError('acceptTerms')}
          </p>
        ) : null}

        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.marketingConsent}
            onChange={(event) => update('marketingConsent', event.target.checked)}
          />
          <span>Send meg gjerne tips til planleggingen på e-post.</span>
        </label>

        <button
          className="btn btn-primary btn-block btn-lg"
          type="submit"
          disabled={busy}
          style={{ marginTop: '0.5rem' }}
        >
          {busy ? 'Oppretter konto …' : 'Opprett konto'}
        </button>

        <p className="small muted" style={{ marginTop: '1.25rem' }}>
          Har dere allerede konto? <Link to="/logg-inn">Logg inn</Link>
        </p>
      </form>
    </AuthLayout>
  );
}

/**
 * Passordfelt med et øye som viser det som er skrevet.
 *
 * Synligheten styres utenfra, så hvert felt på siden har sin egen: å vise det
 * ene skal ikke avsløre det andre. Knappen er `type="button"`, ellers sender
 * den inn skjemaet den står i.
 */
function PasswordInput({
  visible,
  onToggle,
  ...input
}: {
  visible: boolean;
  onToggle: () => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'>) {
  return (
    <div className="passordfelt">
      <input className="input" type={visible ? 'text' : 'password'} {...input} />
      <button
        type="button"
        className="passordfelt-vis"
        onClick={onToggle}
        aria-label={visible ? 'Skjul passord' : 'Vis passord'}
      >
        <Ikon navn={visible ? 'oyeLukket' : 'oye'} />
      </button>
    </div>
  );
}
